import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { CalendarIcon, Check, Loader2, Users, MessageCircle, Info, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CONTACTS } from "@/data/contacts";
import ManageReservationDialog from "@/components/ManageReservationDialog";
import { useLang } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  surname: "",
  phone: "",
  email: "",
  people: 2,
  notes: "",
};

function buildWhatsappText(r) {
  const dateFmt = format(new Date(r.date), "d MMMM yyyy", { locale: it });
  const lines = [
    "*NUOVA PRENOTAZIONE — Drago Ramen*",
    "————————————————",
    `*Nome:* ${r.name} ${r.surname}`,
    `*Telefono:* ${r.phone}`,
    `*Email:* ${r.email}`,
    `*Data:* ${dateFmt}`,
    `*Ora:* ${r.time}`,
    `*Persone:* ${r.people}`,
    `*Codice:* ${r.id.slice(0, 8).toUpperCase()}`,
  ];
  if (r.notes) { lines.push(""); lines.push(`_Note: ${r.notes}_`); }
  return lines.join("\n");
}

export default function BookingForm() {
  const [form, setForm] = useState(initialForm);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [manageOpen, setManageOpen] = useState(false);
  const { t } = useLang();

  const dateStr = useMemo(() => (date ? format(date, "yyyy-MM-dd") : ""), [date]);

  useEffect(() => {
    if (!dateStr) return;
    let cancelled = false;
    setLoadingSlots(true);
    setTime("");
    axios.get(`${API}/reservations/slots`, { params: { date: dateStr } })
      .then((r) => { if (!cancelled) setSlots(r.data.slots || []); })
      .catch(() => { if (!cancelled) setSlots([]); })
      .finally(() => { if (!cancelled) setLoadingSlots(false); });
    return () => { cancelled = true; };
  }, [dateStr]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));

  const validate = () => {
    if (!form.name.trim() || !form.surname.trim()) return "Nome e cognome obbligatori.";
    if (!/^[+0-9\s\-()]{5,}$/.test(form.phone)) return "Telefono non valido.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Email non valida.";
    if (!dateStr) return "Seleziona una data.";
    if (!time) return "Seleziona un orario.";
    return null;
  };

  const submit = (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    // Build WhatsApp URL FIRST (synchronous)
    const tempCode = Math.random().toString(36).slice(2, 10).toUpperCase();
    const previewRes = {
      id: tempCode, name: form.name.trim(), surname: form.surname.trim(),
      phone: form.phone.trim(), email: form.email.trim(),
      date: dateStr, time, people: Number(form.people), notes: form.notes.trim(),
    };
    const text = buildWhatsappText(previewRes);
    const waUrl = `https://wa.me/${CONTACTS.cellIntl}?text=${encodeURIComponent(text)}`;

    // Fire-and-forget: save to DB in background (non-blocking)
    setSubmitting(true);
    axios.post(`${API}/reservations`, {
      name: form.name.trim(), surname: form.surname.trim(),
      phone: form.phone.trim(), email: form.email.trim(),
      date: dateStr, time, people: Number(form.people), notes: form.notes.trim(),
    }).then(({ data }) => {
      setSuccess({ res: data, waUrl: `https://wa.me/${CONTACTS.cellIntl}?text=${encodeURIComponent(buildWhatsappText(data))}` });
    }).catch(() => {
      setSuccess({ res: previewRes, waUrl });
    }).finally(() => setSubmitting(false));

    // Redirect to WhatsApp IMMEDIATELY (synchronous, user-initiated → not blocked)
    window.location.href = waUrl;
  };

  const resetForm = () => { setForm(initialForm); setDate(null); setTime(""); setSuccess(null); };

  return (
    <section id="prenota" data-testid="booking-section" className="relative py-28 md:py-40 px-6 md:px-10 bg-smoke">
      <div className="max-w-6xl mx-auto grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-24 items-start">
        <div className="lg:sticky lg:top-32">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-gold" />
            <span className="text-xs uppercase tracking-[0.32em] text-gold font-medium">{t("booking_overline")}</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold leading-[1.05] tracking-tight"
            data-testid="booking-title"
          >
            {t("booking_title")}
          </motion.h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            {t("booking_intro")}
          </p>

          <div className="mt-8 rounded-2xl bg-ash/60 border border-gold/20 p-5">
            <div className="flex items-start gap-3">
              <Info size={16} className="text-gold mt-0.5 shrink-0" />
              <div className="text-sm text-white/70 leading-relaxed">
                <div className="text-gold font-medium mb-1">{t("booking_how_title")}</div>
                {t("booking_how_body")}
              </div>
            </div>
          </div>

          <div className="mt-6 space-y-3 text-sm text-white/60">
            <div>· {t("booking_slot_note")}</div>
            <div>· <span className="text-gold">{t("booking_code_note")}</span></div>
          </div>

          <button
            type="button"
            onClick={() => setManageOpen(true)}
            data-testid="booking-manage-btn"
            className="mt-8 inline-flex items-center gap-2 border border-gold/40 hover:bg-gold hover:text-ink text-gold text-sm px-5 py-2.5 rounded-full transition-colors"
          >
            <Settings2 size={14} /> {t("booking_manage")}
          </button>
        </div>

        <div className="relative">
          {success ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="rounded-3xl bg-gradient-to-b from-ash to-smoke border border-gold/30 p-10 md:p-12 relative overflow-hidden"
              data-testid="booking-success"
            >
              <div className="w-14 h-14 rounded-full grid place-items-center bg-gold/15 border border-gold/40 mb-6">
                <Check className="text-gold" size={26} />
              </div>
              <h3 className="font-display text-3xl md:text-4xl font-semibold">Richiesta ricevuta!</h3>
              <p className="mt-4 text-white/70">
                La tua prenotazione è <span className="text-gold">in attesa di conferma</span>. Il ristorante ti contatterà a breve al numero fornito.
              </p>
              <div className="mt-4 text-white/60 text-sm">
                Ciao <span className="text-white">{success.res.name}</span>: ti aspettiamo il{" "}
                <span className="text-gold">{format(new Date(success.res.date), "d MMMM yyyy", { locale: it })}</span> alle{" "}
                <span className="text-gold">{success.res.time}</span> per {success.res.people} persone.
              </div>
              <div className="mt-8 grid grid-cols-2 gap-3 text-sm">
                <div className="p-4 rounded-xl bg-black/40 border border-white/5">
                  <div className="text-white/50 text-xs uppercase tracking-widest">Codice</div>
                  <div className="mt-1 font-mono text-white truncate" data-testid="booking-success-code">{success.res.id.slice(0, 8).toUpperCase()}</div>
                </div>
                <div className="p-4 rounded-xl bg-black/40 border border-white/5">
                  <div className="text-white/50 text-xs uppercase tracking-widest">Stato</div>
                  <div className="mt-1 text-amber-300">In attesa</div>
                </div>
              </div>
              <div className="mt-6 text-xs text-white/50">
                Salva il codice — ti servirà per modificare o annullare la prenotazione.
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={success.waUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="booking-open-whatsapp"
                  className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1FBA57] text-white px-6 py-3 rounded-full text-sm transition-colors"
                >
                  <MessageCircle size={15} /> Apri WhatsApp
                </a>
                <button
                  onClick={resetForm}
                  data-testid="booking-new-btn"
                  className="inline-flex items-center gap-2 border border-white/25 hover:border-white text-white hover:bg-white hover:text-ink px-6 py-3 rounded-full text-sm transition-colors duration-300"
                >
                  Nuova prenotazione
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.form
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
              onSubmit={submit}
              className="rounded-3xl bg-ash/70 backdrop-blur-md border border-white/10 p-8 md:p-10"
              data-testid="booking-form"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Nome" testId="field-name">
                  <input required value={form.name} onChange={setField("name")} data-testid="booking-input-name" className={inputCls} placeholder="Marco" />
                </Field>
                <Field label="Cognome" testId="field-surname">
                  <input required value={form.surname} onChange={setField("surname")} data-testid="booking-input-surname" className={inputCls} placeholder="Rossi" />
                </Field>
                <Field label="Telefono" testId="field-phone">
                  <input required value={form.phone} onChange={setField("phone")} data-testid="booking-input-phone" className={inputCls} placeholder="+39 344 417 4626" inputMode="tel" />
                </Field>
                <Field label="Email" testId="field-email">
                  <input required type="email" value={form.email} onChange={setField("email")} data-testid="booking-input-email" className={inputCls} placeholder="tu@email.com" />
                </Field>

                <Field label="Data" testId="field-date">
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" data-testid="booking-input-date" className={`${inputCls} text-left flex items-center justify-between`}>
                        <span className={date ? "text-white" : "text-white/40"}>
                          {date ? format(date, "d MMMM yyyy", { locale: it }) : "Seleziona data"}
                        </span>
                        <CalendarIcon size={16} className="text-white/50" />
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-ink border-white/10" align="start">
                      <Calendar mode="single" selected={date} onSelect={setDate}
                        disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                        initialFocus className="bg-ink text-white" />
                    </PopoverContent>
                  </Popover>
                </Field>

                <Field label="Persone" testId="field-people">
                  <div className="relative">
                    <Users size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                    <select required value={form.people} onChange={setField("people")} data-testid="booking-input-people" className={`${inputCls} pl-10 appearance-none cursor-pointer`}>
                      {Array.from({ length: 30 }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n} className="bg-ink">{n} {n === 1 ? "persona" : "persone"}</option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>

              <div className="mt-7" data-testid="field-time">
                <label className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-3">Orario</label>
                {!dateStr && <p className="text-sm text-white/40">Seleziona prima una data per vedere gli orari disponibili.</p>}
                {dateStr && loadingSlots && <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 size={14} className="animate-spin" /> Carico orari...</div>}
                {dateStr && !loadingSlots && (
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {slots.map((s) => {
                      const disabled = s.full;
                      const active = time === s.time;
                      return (
                        <button key={s.time} type="button" disabled={disabled} onClick={() => setTime(s.time)} data-testid={`booking-slot-${s.time}`}
                          className={`px-3 py-2.5 rounded-xl text-sm border transition-colors duration-200 ${
                            active ? "bg-ember border-ember text-white"
                              : disabled ? "bg-black/30 border-white/5 text-white/25 cursor-not-allowed line-through"
                              : "bg-black/40 border-white/10 hover:border-gold text-white/85"
                          }`}>
                          {s.time}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <Field label="Richieste particolari" testId="field-notes" className="mt-7">
                <textarea value={form.notes} onChange={setField("notes")} data-testid="booking-input-notes" rows={3} className={`${inputCls} resize-none`} placeholder="Allergie, tavolo tranquillo, occasione speciale..." />
              </Field>

              <button type="submit" disabled={submitting} data-testid="booking-submit-btn"
                className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-ember hover:bg-emberDeep disabled:opacity-60 disabled:cursor-not-allowed text-white px-8 py-4 rounded-full font-medium tracking-wide transition-colors duration-300 shadow-[0_10px_40px_-10px_rgba(200,16,46,0.6)]">
                {submitting ? <><Loader2 size={18} className="animate-spin" /> {t("booking_submitting")}</> : <><MessageCircle size={17} /> {t("booking_submit")}</>}
              </button>
              <p className="mt-4 text-xs text-white/40 text-center">
                {t("booking_submit_hint")}
              </p>
            </motion.form>
          )}
        </div>
      </div>
      <ManageReservationDialog open={manageOpen} onOpenChange={setManageOpen} />
    </section>
  );
}

const inputCls =
  "w-full bg-black/40 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-white/35 outline-none focus:border-gold focus:ring-2 focus:ring-gold/20 transition-colors duration-200";

function Field({ label, children, testId, className = "" }) {
  return (
    <label className={`block ${className}`} data-testid={testId}>
      <span className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">{label}</span>
      {children}
    </label>
  );
}
